import { inject, Injectable } from '@angular/core';
import configureMeasurements from 'convert-units';
import allMeasures, { AllMeasures, AllMeasuresSystems, AllMeasuresUnits } from 'convert-units/definitions/all';
import { round } from 'lodash';
import { environment } from '../../../environments/environment';
import { LanguageService } from '../../core/services/language.service';
import { UserConfigurationService } from '../../core/services/user-configuration.service';
import { DimensionSystem } from '../enums/dimension-system.enum';
import { TemperatureUnitsExtension } from '../enums/temperature-unit-extension';
import { FormatService } from './format.service';
import { TransferItemUnit } from '../../pages/transfers/enum/transfer-item-unit.enum';

type DimensionTranslationsMap = {
  [key in AllMeasuresUnits]: {
    metric: string;
    imperial: string;
  };
};

@Injectable({ providedIn: 'root' })
export class DimensionSystemService {
  private userConfigurationService = inject(UserConfigurationService);
  private languageService = inject(LanguageService);
  private formatService = inject(FormatService);

  private readonly measurementConvert = configureMeasurements<AllMeasures, AllMeasuresSystems, AllMeasuresUnits>(
    allMeasures,
  );
  private readonly supportedTypes = new Set(
    this.measurementConvert()
      .list()
      .map(({ abbr }) => abbr),
  );

  // TODO: "dimensionTranslationsMap" has more items than "dimensionUnitImperialPair". I think, their amount should be the same
  private readonly dimensionTranslationsMap = {
    ['pcs']: {
      metric: this.translate('general.units.piece.s_pl'),
      imperial: this.translate('general.units.piece.s_pl'),
    },
    ['km']: {
      metric: this.translate('general.units.distance.km.abbr'),
      imperial: this.translate('general.units.distance.mi.abbr'),
    },
    ['m']: {
      metric: this.translate('general.units.distance.m.s'),
      imperial: this.translate('general.units.distance.ft.abbr'),
    },
    ['cm']: {
      metric: this.translate('general.units.distance.cm.abbr'),
      imperial: this.translate('general.units.distance.in.abbr'),
    },
    ['mt']: {
      metric: this.translate('general.units.weight.t.abbr'),
      imperial: this.translate('general.units.weight.t.abbr'),
    },
    ['kg']: {
      metric: this.translate('general.units.weight.kg.abbr'),
      imperial: this.translate('general.units.weight.lb.abbr'),
    },
    ['g']: {
      metric: this.translate('general.units.weight.g.abbr'),
      imperial: this.translate('general.units.weight.oz.abbr'),
    },
    ['l']: {
      metric: this.translate('general.units.volume.l.abbr'),
      imperial: this.translate('general.units.volume.qt.abbr'),
    },
    ['ml']: {
      metric: this.translate('general.units.volume.ml.abbr'),
      imperial: this.translate('general.units.volume.flOz.abbr'),
    },
    ['m3']: {
      metric: this.translate('general.units.volume.cbm.abbr'),
      imperial: this.translate('general.units.volume.ft3.abbr'),
    },
    ['ream']: {
      metric: this.translate('general.units.pack.s_pl'),
      imperial: this.translate('general.units.pack.s_pl'),
    },
    ['km/h']: {
      metric: this.translate('general.units.speed.kmh'),
      imperial: this.translate('general.units.speed.mph'),
    },
    ['C']: {
      metric: this.translate('general.units.temp.c'),
      imperial: this.translate('general.units.temp.f'),
    },
    ['l/h']: {
      metric: this.translate('general.units.volume.l_per_hour.abbr'),
      imperial: this.translate('general.units.volume.gal_per_hour.abbr'),
    },
  } as DimensionTranslationsMap;

  // TODO: potential place for normalization of units that might have different representation like: '°C', 'C°', 'C' for Celsius
  private dimensionUnitNormalizationMap: Record<string | AllMeasuresUnits | TransferItemUnit, AllMeasuresUnits> = {
    [TransferItemUnit.PIECE]: 'pcs',
    [TransferItemUnit.METER]: 'm',
    [TransferItemUnit.CENTIMETER]: 'cm',
    [TransferItemUnit.TONNE]: 'mt',
    [TransferItemUnit.KILOGRAM]: 'kg',
    [TransferItemUnit.GRAM]: 'g',
    [TransferItemUnit.LITER]: 'l',
    [TransferItemUnit.MILLILITER]: 'ml',
    [TransferItemUnit.CUBIC_METER]: 'm3',
    [TransferItemUnit.PACKAGE]: 'ream',
    [TemperatureUnitsExtension.CELSIUS_DEGREE]: 'C',
    [TemperatureUnitsExtension.DEGREE_CELSIUS]: 'C',
  };
  private dimensionUnitImperialPair: Partial<Record<AllMeasuresUnits, AllMeasuresUnits>> = {
    km: 'mi',
    'km/h': 'mph',
    m: 'ft',
    cm: 'in',
    mt: 't',
    kg: 'lb',
    g: 'oz',
    l: 'qt',
    ml: 'fl-oz',
    m3: 'ft3',
    C: 'F',
  };

  // convert and return a string representation of dimension unit ('km' -> 'mi')
  public toUserDimensionUnit(unit: string | AllMeasuresUnits | TransferItemUnit): string {
    const valueUnitType = this.normalizeUnitType(unit);
    const dimensionUnitNames = this.dimensionTranslationsMap[valueUnitType];

    if (dimensionUnitNames) {
      return this.isMetricSystem() ? dimensionUnitNames.metric : dimensionUnitNames.imperial;
    }

    return unit;
  }

  // convert and return a number value of measure (10000 (km)-> 6213.71 (mi))
  public toUserMeasure(
    value: number | undefined,
    systemUnitDimension: string | AllMeasuresUnits | TransferItemUnit,
    precision: number = environment.DIGITS_PRECISION_DEFAULT,
  ): number {
    if (!value) return 0;

    const valueUnitType = this.normalizeUnitType(systemUnitDimension);
    const targetUnitType = this.isMetricSystem() ? valueUnitType : this.resolvePairImperialUnit(valueUnitType);

    const convertedValue =
      valueUnitType !== targetUnitType ? this.measurementConvert(value).from(valueUnitType).to(targetUnitType) : value;
    return round(convertedValue, precision);
  }

  // reverse operation of 'toUserMeasure'
  public toSystemMeasure(
    value: number,
    systemUnitDimension: string | AllMeasuresUnits | TransferItemUnit,
    precision: number = environment.DIGITS_PRECISION_DEFAULT,
  ): number {
    if (!value || this.isMetricSystem()) {
      return round(value, precision);
    }

    const valueUnitType = this.normalizeUnitType(systemUnitDimension);
    const sourceUnitType = this.resolvePairImperialUnit(valueUnitType);

    if (valueUnitType !== sourceUnitType) {
      const convertedValue = this.measurementConvert(value).from(sourceUnitType).to(valueUnitType);
      return round(convertedValue, precision);
    }

    return round(value, precision);
  }

  // convert and return a string value of measure (if 'km' and 'en-US', then 10000 -> "6,213.71")
  public toUserMeasureI18n(
    value: number,
    systemUnitDimension: string | AllMeasuresUnits | TransferItemUnit,
    precision: number = environment.DIGITS_PRECISION_DEFAULT,
  ): string {
    if (!value) return '0';

    const convertedNumber = this.toUserMeasure(value, systemUnitDimension, precision);
    const formattedNumber = this.formatService.formatNumber(convertedNumber, precision);

    return formattedNumber || convertedNumber.toString();
  }

  public isUnsupportedType(unit: string): boolean {
    return !this.supportedTypes.has(this.normalizeUnitType(unit));
  }

  private isMetricSystem(): boolean {
    return this.userConfigurationService.getUserDimensionSystem() === DimensionSystem.METRIC;
  }

  private normalizeUnitType(unit: string | AllMeasuresUnits | TransferItemUnit): AllMeasuresUnits {
    return this.dimensionUnitNormalizationMap[unit] ?? unit;
  }

  private resolvePairImperialUnit(valueUnitType: AllMeasuresUnits): AllMeasuresUnits {
    return this.dimensionUnitImperialPair[valueUnitType] ?? valueUnitType;
  }

  private translate(key: string): string {
    return this.languageService.getInstant(key);
  }
}
